import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChatUatApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import {
  ASANA_OAUTH_CONNECTED_KEY,
  ASANA_OAUTH_MAX_AGE_MS,
  ASANA_OAUTH_RETURN_KEY,
  ASANA_OAUTH_SESSION_ID_KEY,
  ASANA_OAUTH_STARTED_AT_KEY,
  ASANA_OAUTH_STATE_KEY,
} from "../../hooks/useAsanaAuthorization";

const DEFAULT_RETURN_PATH = "/dashboard/uat/chat?view=validate";

function safeReturnPath(value: string | null): string {
  if (
    value === "/dashboard/uat/chat" ||
    value?.startsWith("/dashboard/uat/chat?")
  ) {
    return value;
  }
  return DEFAULT_RETURN_PATH;
}

export default function AsanaOAuthCallbackPage() {
  const api = useMemo(() => ChatUatApiFactory(), []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const returnedState = searchParams.get("state");
    const sessionId = searchParams.get("session_id");
    const expectedState = sessionStorage.getItem(ASANA_OAUTH_STATE_KEY);
    const expectedSessionId = sessionStorage.getItem(
      ASANA_OAUTH_SESSION_ID_KEY
    );
    const startedAt = Number(
      sessionStorage.getItem(ASANA_OAUTH_STARTED_AT_KEY)
    );
    const returnPath = safeReturnPath(
      sessionStorage.getItem(ASANA_OAUTH_RETURN_KEY)
    );
    const authorizationIsFresh =
      Number.isSafeInteger(startedAt) &&
      startedAt <= Date.now() &&
      Date.now() - startedAt <= ASANA_OAUTH_MAX_AGE_MS;

    if (
      !expectedState ||
      !expectedSessionId ||
      !authorizationIsFresh ||
      (returnedState !== null && returnedState !== expectedState) ||
      sessionId !== expectedSessionId
    ) {
      setError("This Asana authorization response is invalid or expired.");
      return;
    }

    let active = true;
    api
      .completeChatUatAsanaAuthorization({ sessionId })
      .then(() => {
        if (!active) {
          return;
        }
        sessionStorage.removeItem(ASANA_OAUTH_STATE_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_RETURN_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_STARTED_AT_KEY);
        sessionStorage.removeItem(ASANA_OAUTH_SESSION_ID_KEY);
        sessionStorage.setItem(ASANA_OAUTH_CONNECTED_KEY, "true");
        navigate(returnPath, { replace: true });
      })
      .catch(() => {
        if (active) {
          setError("UAT Insights could not complete Asana authorization.");
        }
      });

    return () => {
      active = false;
    };
  }, [api, navigate, searchParams]);

  return (
    <Box padding="xxl">
      <Container header={<Header variant="h1">Asana authorization</Header>}>
        {error ? (
          <SpaceBetween size="m">
            <Alert type="error" header="Connection failed">
              {error}
            </Alert>
            <Button
              variant="primary"
              onClick={() => navigate(DEFAULT_RETURN_PATH, { replace: true })}
            >
              Return to UAT Validation
            </Button>
          </SpaceBetween>
        ) : (
          <StatusIndicator type="loading">
            Completing Asana authorization
          </StatusIndicator>
        )}
      </Container>
    </Box>
  );
}
